import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "cookie-consent-v1";

const defaultPrefs = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

export const CookieConsentContext = createContext({
  isBannerOpen: false,
  prefs: defaultPrefs,
  openBanner: () => {},
  closeBanner: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  savePrefs: (_prefs) => {},
});

export const CookieConsentProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [isBannerOpen, setIsBannerOpen] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setPrefs({ ...defaultPrefs, ...parsed });
          setIsBannerOpen(false);
          return;
        }
      }
    } catch (_) {}
    // Show banner after initial mount
    const t = setTimeout(() => setIsBannerOpen(true), 0);
    return () => clearTimeout(t);
  }, []);

  const persist = useCallback((next) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
  }, []);

  const openBanner = useCallback(() => setIsBannerOpen(true), []);
  const closeBanner = useCallback(() => setIsBannerOpen(false), []);

  const acceptAll = useCallback(() => {
    const next = { ...defaultPrefs, analytics: true, marketing: true, functional: true };
    setPrefs(next);
    persist(next);
    setIsBannerOpen(false);
    try {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'analytics_storage': 'granted'
        });
      }
    } catch(_) {}
  }, [persist]);

  const rejectAll = useCallback(() => {
    const next = { ...defaultPrefs, analytics: false, marketing: false, functional: false };
    setPrefs(next);
    persist(next);
    setIsBannerOpen(false);
    try {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'analytics_storage': 'denied'
        });
      }
    } catch(_) {}
  }, [persist]);

  const savePrefs = useCallback((nextPrefs) => {
    const next = { ...defaultPrefs, ...nextPrefs };
    setPrefs(next);
    persist(next);
    setIsBannerOpen(false);
    try {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': next.marketing ? 'granted' : 'denied',
          'ad_user_data': next.marketing ? 'granted' : 'denied',
          'ad_personalization': next.marketing ? 'granted' : 'denied',
          'analytics_storage': next.analytics ? 'granted' : 'denied'
        });
      }
    } catch(_) {}
  }, [persist]);

  const value = useMemo(() => ({
    isBannerOpen,
    prefs,
    openBanner,
    closeBanner,
    acceptAll,
    rejectAll,
    savePrefs,
  }), [isBannerOpen, prefs, openBanner, closeBanner, acceptAll, rejectAll, savePrefs]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => React.useContext(CookieConsentContext);


